import {
  Anchor,
  Badge,
  Box,
  Center,
  createStyles,
  Group,
  HoverCard,
  Text,
} from '@mantine/core';
import React from 'react';
import { Link } from 'react-router-dom';

import { DeviceDetailsType } from '@portals/api/organizations';
import { C2C_INTEGRATIONS } from '@portals/framework';
import { ReactComponent as ArrowRight } from '@portals/icons/linear/arrow-right-1.svg';
import { ReactComponent as CloudIcon } from '@portals/icons/linear/cloud.svg';
import { ReactComponent as NoCloudIcon } from '@portals/icons/linear/no-cloud.svg';

interface CloudStatusIndicatorProps {
  connector?: DeviceDetailsType['connector'];
}

// NOTE: currently we only support c2c and native devices, when edge devices are added
// we need to backend add edge support

export function CloudConnectorStatusIndicator({
  connector,
}: CloudStatusIndicatorProps) {
  const { classes, theme } = useStyles();

  const isError = connector?.state === 'error';
  const iconColor = isError ? theme.colors.red['4'] : theme.colors.gray['9'];

  const connectorInfo = connector?.name
    ? C2C_INTEGRATIONS[connector.name]
    : null;

  const statusLabel = connector?.state === 'active' ? 'Active' : 'Error';
  const statusColor = connector?.state === 'active' ? 'teal' : 'red';

  function getIconComponent() {
    if (!connector) {
      return CloudIcon;
    }

    return isError ? NoCloudIcon : CloudIcon;
  }

  const IconComponent = getIconComponent();

  const connectorIcon = (
    <Center w={40} h={40} className={classes.iconWrapper}>
      <IconComponent color={iconColor} />
    </Center>
  );

  return (
    <HoverCard
      width={300}
      shadow="md"
      withArrow
      position="bottom"
      styles={(theme) => ({
        dropdown: {
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.xl,
          borderRadius: theme.radius.lg,
          boxShadow: theme.shadows.lg,
          minWidth: 425,
        },
      })}
    >
      <HoverCard.Target>{connectorIcon}</HoverCard.Target>
      <HoverCard.Dropdown p="xl">
        {!connector ? (
          <Text c="gray.9">
            This device is connected directly to the Xyte platform.
          </Text>
        ) : (
          <>
            <Group position="apart">
              <Text fw={500} size="md" c="gray.9">
                Connector
              </Text>

              <Anchor
                component={Link}
                to={`/settings/c2c_connectors/${connector.id}`}
                p="xs"
                className={classes.configLink}
              >
                <Group spacing="xs">
                  <Text>Configuration</Text>

                  <ArrowRight width={16} height={16} />
                </Group>
              </Anchor>
            </Group>

            <Group position="apart">
              <Group spacing="xs">
                {connectorInfo?.logo && (
                  <Box mr={5}>
                    <img
                      src={connectorInfo.logo}
                      alt={connectorInfo.name}
                      width={34}
                      height={34}
                    />
                  </Box>
                )}
                <Text size="sm">{connectorInfo?.name || connector.name}</Text>
              </Group>
              <Badge
                color={statusColor}
                size="lg"
                classNames={{ inner: classes.statusBadgeText }}
                leftSection={
                  <Box
                    w="9px"
                    h="9px"
                    bg={statusColor}
                    className={classes.statusIndicatorDot}
                  />
                }
              >
                {statusLabel}
              </Badge>
            </Group>
          </>
        )}
      </HoverCard.Dropdown>
    </HoverCard>
  );
}

const useStyles = createStyles((theme) => ({
  iconWrapper: {
    borderRadius: '50%',
    border: `1px solid ${theme.colors.gray[4]}`,
    flexShrink: 0,
  },
  configLink: {
    display: 'block',
    borderRadius: theme.radius.sm,
  },
  statusIndicatorDot: {
    borderRadius: '50%',
  },
  statusBadgeText: {
    color: theme.colors.gray[9],
    fontWeight: 400,
  },
}));
